import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import AiPromptGenerator from "@/components/resume-builder/AiPromptGenerator";

const FIELD_CLASS = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400";
const EMPTY_ENTRY = { title: "", tech_stack: "", link: "", start_date: "", end_date: "", bullets: [] };

function ProjectEntryCard({ entry, idx, onChange, onRemove }) {
  const [suggesting, setSuggesting] = useState(false);

  const update = (field, value) => onChange(idx, { ...entry, [field]: value });

  const suggestBullets = async () => {
    if (!entry.title.trim()) {
      toast.error("Add a project title first");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/resume-builder/suggest-bullets", { job_title: entry.title, company: entry.tech_stack, context: "This is a personal/academic project, not a job." });
      update("bullets", [...entry.bullets, ...data.bullets]);
    } catch (e) {
      toast.error("Could not suggest bullets right now");
    } finally {
      setSuggesting(false);
    }
  };

  const updateBullet = (i, value) => update("bullets", entry.bullets.map((b, bi) => (bi === i ? value : b)));
  const removeBullet = (i) => update("bullets", entry.bullets.filter((_, bi) => bi !== i));

  return (
    <div className="bg-white/[0.03] rounded-xl p-4" data-testid={`resume-project-entry-${idx}`}>
      <div className="flex justify-end mb-2">
        <button onClick={() => onRemove(idx)} data-testid={`resume-remove-project-${idx}`} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={entry.title} onChange={(e) => update("title", e.target.value)} placeholder="Project Title" data-testid={`resume-project-title-${idx}`} className={FIELD_CLASS} />
        <input value={entry.tech_stack} onChange={(e) => update("tech_stack", e.target.value)} placeholder="Tech Stack (e.g. React, Node.js)" data-testid={`resume-project-techstack-${idx}`} className={FIELD_CLASS} />
        <input value={entry.link} onChange={(e) => update("link", e.target.value)} placeholder="Project Link (optional)" data-testid={`resume-project-link-${idx}`} className={FIELD_CLASS} />
        <div className="flex gap-2">
          <input value={entry.start_date} onChange={(e) => update("start_date", e.target.value)} placeholder="Start" data-testid={`resume-project-start-${idx}`} className={FIELD_CLASS + " flex-1"} />
          <input value={entry.end_date} onChange={(e) => update("end_date", e.target.value)} placeholder="End" data-testid={`resume-project-end-${idx}`} className={FIELD_CLASS + " flex-1"} />
        </div>
      </div>

      <div className="space-y-1.5 mb-2">
        {entry.bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={b} onChange={(e) => updateBullet(i, e.target.value)} data-testid={`resume-project-bullet-${idx}-${i}`} className={FIELD_CLASS + " flex-1"} />
            <button onClick={() => removeBullet(i)} data-testid={`resume-remove-project-bullet-${idx}-${i}`} className="text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button onClick={suggestBullets} disabled={suggesting} data-testid={`resume-suggest-project-bullets-${idx}`} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-60">
        {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Suggest Highlights
      </button>
    </div>
  );
}

export default function ProjectsSection({ draft, setDraft }) {
  const updateEntry = (idx, entry) => setDraft({ ...draft, projects: draft.projects.map((e, i) => (i === idx ? entry : e)) });
  const removeEntry = (idx) => setDraft({ ...draft, projects: draft.projects.filter((_, i) => i !== idx) });
  const addEntry = () => setDraft({ ...draft, projects: [...draft.projects, { ...EMPTY_ENTRY }] });

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="resume-section-projects">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-white flex items-center gap-1.5">Projects <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-indigo-300 bg-indigo-400/10 border border-indigo-400/25 rounded-full px-1.5 py-0.5"><Sparkles className="w-2.5 h-2.5" /> AI powered</span></p>
        <button onClick={addEntry} data-testid="resume-add-project-button" className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
          <Plus className="w-3.5 h-3.5" /> Add Project
        </button>
      </div>
      <div className="space-y-3">
        {draft.projects.map((entry, idx) => (
          <ProjectEntryCard key={idx} entry={entry} idx={idx} onChange={updateEntry} onRemove={removeEntry} />
        ))}
        {draft.projects.length === 0 && <p className="text-xs text-slate-400">No projects added yet.</p>}
      </div>
      <div className="mt-3">
        <AiPromptGenerator section="projects" draft={draft} setDraft={setDraft} />
      </div>
    </div>
  );
}
